/**
 * ui/primitives.tsx — ส่วนประกอบพื้นฐานที่ทุกหน้าใช้
 *
 * ตั้งใจให้เล็กและตรงไปตรงมา ไม่พึ่ง component library ภายนอก
 * เพราะสไตล์ทั้งหมดมาจาก @ft/ui-tokens และหน้าตาที่ต้องการเรียบมาก
 */

import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { statusColor, type StatusKey } from '@ft/ui-tokens';
import { cn } from '~/lib/cn';

// ==================== ปุ่ม ====================

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  /** แสดงตัวหมุนและปิดการกด ระหว่างรอผลจากเซิร์ฟเวอร์ */
  loading?: boolean;
  iconOnly?: boolean;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, iconOnly = false, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'btn',
        VARIANT_CLASS[variant],
        size === 'sm' && 'btn-sm',
        iconOnly && 'btn-icon',
        className,
      )}
      disabled={disabled || loading}
      // บอก screen reader ว่ากำลังทำงาน ไม่ใช่ปุ่มเสีย
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 size={15} className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

// ==================== ช่องกรอก ====================

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn('field', invalid && 'field-error', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn('field', invalid && 'field-error', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn('field pr-8', invalid && 'field-error', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {children}
    </select>
  );
});

// ==================== ห่อช่องกรอกพร้อมป้ายและข้อความผิดพลาด ====================

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, required, className, children }: FieldProps) {
  return (
    <div className={className}>
      <label className="label" htmlFor={htmlFor}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {children}
      {/* role=alert ให้ screen reader อ่านทันทีเมื่อ validation ไม่ผ่าน */}
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="hint">{hint}</p>
      ) : null}
    </div>
  );
}

// ==================== การ์ด ====================

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('card', className)}>{children}</div>;
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="card-header">
      <div className="min-w-0">
        <h2 className="card-title">{title}</h2>
        {description ? <p className="mt-0.5 text-xs text-fg-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// ==================== ป้ายสถานะ ====================

const STATUS_LABEL: Record<StatusKey, string> = {
  draft: 'แบบร่าง',
  sent: 'ส่งแล้ว',
  accepted: 'อนุมัติ',
  rejected: 'ปฏิเสธ',
  issued: 'ออกแล้ว',
  paid: 'ชำระแล้ว',
  cancelled: 'ยกเลิก',
};

export function StatusBadge({ status }: { status: string }) {
  const key = (status in statusColor ? status : 'draft') as StatusKey;
  const color = statusColor[key];

  return (
    <span className="badge" style={{ color: color.fg, backgroundColor: color.bg }}>
      {STATUS_LABEL[key]}
    </span>
  );
}

// ==================== สถานะว่าง / กำลังโหลด ====================

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      {icon ? <div className="mb-1 text-fg-subtle">{icon}</div> : null}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description ? <p className="max-w-sm text-xs text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/** แถบเทาแทนเนื้อหาที่กำลังโหลด ลดการกระตุกของ layout */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-muted', className)} />;
}

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-[var(--border)]">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 px-3 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-4', columnIndex === 1 ? 'flex-1' : 'w-20')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ==================== แถบข้อความแจ้งเตือนในหน้า ====================

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children?: ReactNode;
}) {
  const toneClass = {
    info: 'bg-accent-soft text-accent-fg',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  }[tone];

  return (
    <div className={cn('rounded-md px-3 py-2.5 text-xs', toneClass)} role="alert">
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
    </div>
  );
}
